import { PlusIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import type {
  DataTableAction,
  DataTablePagination,
  SortOrder,
} from '@/components/data-table/data-table.interface'
import { ConfirmDialog } from '@/components/molecules/confirm-dialog.molecule'
import { Button } from '@/components/ui/button'

import { PromptTemplateDrawer } from './prompt-template-drawer.organism'
import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'
import { PromptTemplateTable } from './prompt-template-table.organism'

interface PromptTemplateTablePanelProps {
  paginatedData: PromptTemplate[]
  processedTotalCount: number
  searchValue: string
  sortBy: string
  sortOrder: SortOrder
  pagination: DataTablePagination
  isDrawerOpen: boolean
  editingTemplate: PromptTemplate | null
  templateToDelete: PromptTemplate | null
  actionList: DataTableAction<PromptTemplate>[]
  isLoading?: boolean
  setSearchValue: (value: string) => void
  handleSortChange: (sortBy: string, sortOrder: SortOrder) => void
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleCreate: () => void
  handleSave: (values: PromptTemplateDrawerFormSchemaType) => void
  setIsDrawerOpen: (isOpen: boolean) => void
  handleDeleteConfirm: () => void
  handleDeleteCancel: () => void
}

/**
 * Composable panel combining PromptTemplateTable with create button and drawer.
 * Used wherever prompt templates need to be listed and managed.
 */
export const PromptTemplateTablePanel = (props: PromptTemplateTablePanelProps): ReactNode => (
  <>
    <PromptTemplateTable
      data={props.paginatedData}
      pagination={{ ...props.pagination, totalCount: props.processedTotalCount }}
      onPageChange={props.handlePageChange}
      onPageSizeChange={props.handlePageSizeChange}
      searchValue={props.searchValue}
      onSearchChange={props.setSearchValue}
      sortBy={props.sortBy}
      sortOrder={props.sortOrder}
      onSortChange={props.handleSortChange}
      actionList={props.actionList}
      isLoading={props.isLoading}
      toolbarActions={
        <Button size="sm" onClick={props.handleCreate} className="prompt-template-create-button">
          <PlusIcon className="mr-1 size-4" />
          Create
        </Button>
      }
    />
    <PromptTemplateDrawer
      isOpen={props.isDrawerOpen}
      onOpenChange={props.setIsDrawerOpen}
      template={props.editingTemplate}
      onSave={props.handleSave}
    />
    <ConfirmDialog
      isOpen={props.templateToDelete !== null}
      onOpenChange={(isOpen) => !isOpen && props.handleDeleteCancel()}
      title="Delete Template"
      description={`Are you sure you want to delete "${props.templateToDelete?.name ?? 'this template'}"? This action cannot be undone.`}
      confirmLabel="Delete"
      onConfirm={props.handleDeleteConfirm}
      onCancel={props.handleDeleteCancel}
      variant="destructive"
    />
  </>
)

PromptTemplateTablePanel.displayName = 'PromptTemplateTablePanel'
